import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  CellContext,
  ColumnOrderState,
  ColumnPinningState,
  OnChangeFn,
  PaginationState,
  TableOptions,
  Updater,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
} from "@tanstack/react-table"
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import { arrayMove } from "@dnd-kit/sortable"

import { Cell } from "./components/Cell"
import { Header } from "./components/Header"
import TableBody from "./components/TableBody"
import { TableHead } from "./components/TableHead"
import { Pagination } from "./components/Pagination"
import { useColumns, useData, usePagination } from "./hooks"

import { isColumnFilterable, isColumnSortable } from "./utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

import type { TDrawerConfig } from "~/ui-rtk/constants/drawer"
import {
  type TMetric,
  type TSources,
  type TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"
import { DEFAULT_CELL_WIDTH } from "./constants"
import { TableSummary } from "./components/TableSummary/TableSummary"
import { MetricsMapping } from "~/ui-rtk/constants/metrics-mapping"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"

import useDefaultColumnWidth from "./hooks/useDefaultColumnWidth"
import useUrlSorting from "~/ui-rtk/hooks/url-sorting"
import useUrlPinnedColumns from "~/ui-rtk/hooks/url-pinned-columns"
import { useSummaries } from "~/ui-rtk/hooks/cube"
import useUrlColumnOrder from "~/ui-rtk/hooks/url-column-order"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { ExportButton } from "~/ui-rtk/components/ui/controls"
import TagModal from "./components/TagModal"
import { TagPanel } from "./components/TagPanel"
import useMediaTagAccess from "~/ui-rtk/hooks/media-tag-access"
import {
  useStructuredTagAccess,
  useTagCategories,
} from "~/ui-rtk/hooks/use-structured-tags"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import { useBulkGetTagAssignmentsQuery } from "~/ui-rtk/api/tagAssignmentsApi"

export type TTableVisualizationProps = {
  compareMode?: boolean
  metrics: TMetric[]
  compareUniqueKey?: string
  columnConfig?: TableVisualizationColumn[]
  sources?: TSources
  className?: string
  setPaginationState?: Dispatch<SetStateAction<PaginationState>>
  paginationState: PaginationState
  totalPages?: number
  enablePagination?: boolean
  disableHover?: boolean
  openDrawer?: (config: Partial<TDrawerConfig>) => void
  renderConfig?: Record<string, unknown>
  rowClickInDrawer?: boolean
  /**
   * Override the default row-click behavior. When set, this fires on row
   * click instead of the `openDrawer` + `rowClickInDrawer` path. Used by
   * AdBlaster's Ads / Social Posts tabs to toggle row selection on click
   * rather than open a drawer.
   */
  onRowClick?: (row: TMetric) => void
  /**
   * Fires with the rows the table is currently showing (the server-paginated
   * page after filters and sort) whenever they change. Lets a caller act on
   * "what is on screen" — e.g. the Paid Media bulk creative analysis — without
   * re-running the Cube query. May fire with an identical list on re-render;
   * dedupe on the caller's side.
   */
  onRowsRendered?: (rows: TMetric[]) => void
  dateRange?: [Date, Date]
  compareRange?: [Date, Date]
  chartId: string
  urlParamsPrefix?: string
  summaryWidget?: keyof typeof MetricsMapping
  cubeFilters?: TCubeFilterOptions
  cubeQueryParams?: Record<string, string>
  totalLabelColumn?: string
  dataAggType?: string
  sortBy?: string
  sortDesc?: boolean
  summaryMetrics?: Record<string, unknown>
  hideItemsPerPage?: boolean
  highlightCells?: boolean
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  csvExport?: () => Promise<void>
  isExporting?: boolean
  exportProgress?: { progress: number; message: string }
  manualPagination?: boolean
}

const MULTI_SORTING_ENABLED = false

const getColumnSizingStorageKey = (chartId: string) =>
  `table-visualization-column-sizing-${chartId}`

/**
 * Live tag-assignments map keyed by itemId. Sourced from a single bulk-get
 * subscription at the TableVisualization level. We expose it via context so
 * TagsCell can read fresh data without depending on the column array — which
 * means the cell never remounts (and the TagPanel modal stays open) when tags
 * are saved or AI auto-suggest writes new options.
 */
type TableTagAssignmentsContextValue = {
  assignments: Record<
    string,
    Record<string, Array<{ optionLabel: string; optionColor: string }>>
  >
  isLoading: boolean
}
const TableTagAssignmentsContext =
  createContext<TableTagAssignmentsContextValue>({
    assignments: {},
    isLoading: false,
  })

type TagsCellProps = {
  itemId: string
  itemType: "campaign" | "adset" | "ad"
  adName?: string
  campaignName?: string
  adSetName?: string
}

const TagsCell: React.FC<TagsCellProps> = ({
  itemId,
  itemType,
  adName,
  campaignName,
  adSetName,
}) => {
  // Read categories internally instead of receiving them via prop. The hook
  // hits the same cache that the table-level prefetch and Main shell already
  // warmed; reading here keeps `categories` out of the parent's `tableColumns`
  // memo deps, so a cache invalidation (e.g. after AI auto-suggest creates
  // new options) doesn't rebuild column defs and remount this cell — which
  // was previously closing the open TagPanel modal mid-flow.
  const { categories } = useTagCategories()
  const { assignments, isLoading } = useContext(TableTagAssignmentsContext)
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const itemAssignments = assignments[itemId] ?? {}

  // Resolve each assignment's category slug to its display label (which may be
  // a company alias) so pills show the aliased name, not the raw slug.
  const categoryLabelBySlug = new Map(categories.map(c => [c.slug, c.label]))
  const pills: Array<{
    label: string
    color: string
    categoryLabel?: string
  }> = []
  for (const [slug, rowAssignments] of Object.entries(itemAssignments)) {
    for (const a of rowAssignments) {
      pills.push({
        label: a.optionLabel,
        color: a.optionColor,
        categoryLabel: categoryLabelBySlug.get(slug) ?? formatTagLabel(slug),
      })
    }
  }

  return (
    <div
      onClick={e => e.stopPropagation()}
      className={cn("p-1", isDemoEnabled && "blur-md")}
    >
      <TagPanel
        itemId={itemId}
        itemType={itemType}
        className="w-full"
        preloadedPills={pills}
        categories={categories}
        adName={adName}
        campaignName={campaignName}
        adSetName={adSetName}
        isLoadingPills={isLoading}
      />
    </div>
  )
}

const TableVisualization: React.FC<TTableVisualizationProps> = ({
  metrics,
  compareMode: isComparing,
  compareUniqueKey,
  columnConfig,
  sources,
  className,
  setPaginationState: setPaginationStateFromProps,
  paginationState: paginationStateFromProps,
  totalPages: totalPagesProps = 1,
  enablePagination = false,
  manualPagination: manualPaginationProp,
  disableHover,
  openDrawer = () => null,
  rowClickInDrawer = false,
  onRowClick: onRowClickProp,
  onRowsRendered,
  dateRange,
  compareRange,
  chartId,
  urlParamsPrefix,
  summaryWidget,
  totalLabelColumn,
  cubeFilters,
  cubeQueryParams,
  renderConfig,
  summaryMetrics,
  hideItemsPerPage = false,
  highlightCells = false,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
  sortBy,
  sortDesc,
  csvExport,
  isExporting = false,
  exportProgress,
}) => {
  // ToDo: check if we need internal pagination for something?
  const {
    paginationState: paginationStateFromHook,
    setPaginationState: setPaginationStateFromHook,
    totalPages: totalPagesFromHook,
    showPagination: showPaginationFromHook,
  } = usePagination(metrics.length)
  const { data, noData, compareData } = useData(
    Boolean(isComparing),
    compareUniqueKey,
    metrics,
  )

  const { summaryData, avgData, compareSummaryData, isLoading } =
    summaryWidget && dateRange
      ? useSummaries({
          widget: summaryWidget,
          dateRange,
          compareRange: compareRange ?? undefined,
          cubeFilters,
          cubeQueryParams,
          calculateAvg: highlightCells,
        })
      : {
          summaryData: null,
          compareSummaryData: null,
          isLoading: false,
          avgData: null,
        }

  const summary = useMemo(() => {
    if (summaryMetrics) {
      return summaryMetrics
    }
    if (summaryData) {
      return summaryData
    }
    return null
  }, [summaryData, summaryMetrics])

  const wrapperRef = useRef<HTMLTableElement | null>(null)
  const columns = useColumns(columnConfig, noData, data)

  const { columnOrder, setColumnOrder } = useUrlColumnOrder(
    urlParamsPrefix ?? chartId,
    columns.map(column => column.key),
  )

  // ToDo: check if we need internal pagination for something?
  const totalPages = enablePagination ? totalPagesProps : totalPagesFromHook
  const paginationState = enablePagination
    ? paginationStateFromProps
    : paginationStateFromHook
  const setPagination = enablePagination
    ? setPaginationStateFromProps
    : setPaginationStateFromHook
  const showPagination = enablePagination ? true : showPaginationFromHook

  // Default manualPagination to enablePagination for backward compatibility
  // If enablePagination is true, we assume manual (server-side) pagination unless explicitly set to false
  const manualPagination = manualPaginationProp ?? enablePagination

  const defaultSorting = useMemo(() => {
    const id = sortBy
    const desc = sortDesc ?? true
    return id
      ? [
          {
            id,
            desc,
          },
        ]
      : undefined
  }, [sortBy, sortDesc])

  const { sorting, setSorting } = useUrlSorting(
    urlParamsPrefix ?? chartId,
    defaultSorting,
  )
  const { pinnedColumns: columnPinning, setPinnedColumns: setColumnPinning } =
    useUrlPinnedColumns(urlParamsPrefix ?? chartId)

  const handlePinningChange: OnChangeFn<ColumnPinningState> = (
    columnPinningUpdater: Updater<ColumnPinningState>,
  ) => {
    setColumnPinning(columnPinningUpdater)
  }

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = (
    order: Updater<ColumnOrderState>,
  ) => {
    setColumnOrder(order)
  }

  const defaultColumnWidth = useDefaultColumnWidth(
    columns,
    wrapperRef,
    DEFAULT_CELL_WIDTH,
  )

  const isMediaTagsEnabled = useMediaTagAccess()
  const isStructuredTagsEnabled = useStructuredTagAccess()
  // Note: we don't gate the table render on tag categories loading anymore.
  // The Main shell prefetches the catalog, and TagsCell + TagPanel handle the
  // empty-state internally — gating here was leaving the table stuck on a
  // spinner whenever the categories cache happened to be in a fetching state.

  // Live tag-assignments subscription owned by TableVisualization (always
  // mounted while the table is shown, unlike ChartDataLoader which Chart
  // unmounts after caching the cube data). When a save happens, RTK Query
  // refetches via this subscription, the context value flips, and TagsCell
  // re-renders with new pills — without rebuilding column defs (which would
  // close any open TagPanel modal).
  const tagsItemType = useMemo(() => {
    const tagsCol = columns.find(c => c.type === "tags" && c.taggableId)
    if (!tagsCol?.taggableId) return undefined
    return tagsCol.taggableId.replace(/_id$/, "").replace(/.*\./, "") as
      | "campaign"
      | "adset"
      | "ad"
  }, [columns])

  const tagsItemIdKey = useMemo(() => {
    const tagsCol = columns.find(c => c.type === "tags" && c.taggableId)
    return tagsCol?.taggableId ?? null
  }, [columns])

  const tagItemIds = useMemo(() => {
    if (!isStructuredTagsEnabled || !tagsItemIdKey) return []
    const parts = tagsItemIdKey.split(".")
    const shortKey = parts.length > 1 ? parts[parts.length - 1] : tagsItemIdKey
    const ids: string[] = []
    for (const row of metrics) {
      const v =
        (row as Record<string, unknown>)[tagsItemIdKey] ??
        (row as Record<string, unknown>)[shortKey]
      if (v != null && v !== "" && v !== "undefined") ids.push(String(v))
    }
    return ids
  }, [metrics, tagsItemIdKey, isStructuredTagsEnabled])

  const {
    data: liveTagAssignments,
    isLoading: isLoadingLiveTagAssignments,
    isUninitialized: isLiveTagAssignmentsUninitialized,
  } = useBulkGetTagAssignmentsQuery(
    { itemType: tagsItemType ?? "ad", itemIds: tagItemIds },
    { skip: !tagsItemType || tagItemIds.length === 0 },
  )

  // Loading flag for cells: true on first load AND while we have ids but the
  // query hasn't resolved yet. Goes false once data arrives (subsequent
  // refetches use isFetching, which we ignore so cells don't flash).
  const isLoadingTagAssignments =
    !!tagsItemType &&
    tagItemIds.length > 0 &&
    (isLoadingLiveTagAssignments ||
      (isLiveTagAssignmentsUninitialized && !liveTagAssignments))

  const tagAssignmentsContextValue = useMemo<TableTagAssignmentsContextValue>(
    () => ({
      assignments: liveTagAssignments ?? {},
      isLoading: isLoadingTagAssignments,
    }),
    [liveTagAssignments, isLoadingTagAssignments],
  )

  // Filter out tags column if feature flag is disabled
  // NOTE: We don't filter out disabled columns because they're needed for comparison matching
  const filteredColumns = isMediaTagsEnabled
    ? columns
    : columns.filter(col => col.type !== "tags")

  const tableColumns = useMemo(() => {
    const cols = filteredColumns.map(column => ({
      ...column,
      accessorFn: (row: TMetric) => {
        if (column.render) {
          return column.render(row, renderConfig)
        }
        return row[column.key]
      },
      highlightFn: column.highlightFn,
      id: column.key,
      key: column.key,
      size: column.width ?? defaultColumnWidth,
      enableSorting: isColumnSortable(column.type) && !column.disableSorting,
      enableFiltering:
        isColumnFilterable(column.type) && !column.disableFiltering,
      enableHiding: true, // Enable column visibility control
      header: () => (
        <Header
          type={column.type}
          value={column.header}
          isBrand={column.isBrand}
          isComparing={Boolean(isComparing)}
          className={column.classes?.head}
        />
      ),
      footer: (props: { column: { id: string } }) => props.column.id,
      sortDescFirst: column.type !== "title",
      cell: (props: CellContext<TMetric, unknown>) => {
        // Handle tags column specially
        if (column.type === "tags" && column.taggableId) {
          const rowData = props.row.original
          const tagIdParts = column.taggableId.split(".")
          const shortKey =
            tagIdParts.length > 1
              ? tagIdParts[tagIdParts.length - 1]
              : column.taggableId
          const itemId = String(rowData[column.taggableId] ?? rowData[shortKey])
          // Derive itemType from taggableId: "campaign_id" -> "campaign", "adset_id" -> "adset", "ad_id" -> "ad"
          const itemType = column.taggableId
            .replace(/_id$/, "")
            .replace(/.*\./, "") as "campaign" | "adset" | "ad"

          if (isStructuredTagsEnabled) {
            // Extract names from row data for AI auto-suggest
            // Keys may be short (ad_name) or prefixed (company_media_metrics_ads.ad_name)
            const getVal = (key: string) => {
              for (const k of Object.keys(rowData)) {
                if (k === key || k.endsWith(`.${key}`)) {
                  return rowData[k] as string
                }
              }
              return undefined
            }
            const adName =
              getVal("ad_name") ??
              getVal("campaign_name") ??
              getVal("adset_name")
            const adCampaignName = getVal("campaign_name")
            const adSetNameVal = getVal("adset_name")

            // Tag assignments are pulled live from the TableVisualization-
            // level bulk-get subscription (via TableTagAssignmentsContext)
            // so saves and AI auto-suggest update pills without a refresh.
            return (
              <TagsCell
                itemId={itemId}
                itemType={itemType}
                adName={adName}
                campaignName={adCampaignName}
                adSetName={adSetNameVal}
              />
            )
          }

          return (
            <div onClick={e => e.stopPropagation()} className="p-1">
              <TagModal
                itemId={itemId}
                itemType={itemType}
                className="w-full"
              />
            </div>
          )
        }

        // Handle normal columns
        let compareWith = undefined
        if (isComparing) {
          compareWith = {
            key: compareUniqueKey,
            data: compareData,
          }
        }

        return (
          <Cell
            sources={sources}
            column={column}
            cellContext={props}
            compareWith={compareWith}
          />
        )
      },
    }))

    return cols
  }, [
    filteredColumns,
    defaultColumnWidth,
    isComparing,
    compareUniqueKey,
    compareData,
    sources,
    renderConfig,
    isStructuredTagsEnabled,
  ])

  const storageKey = getColumnSizingStorageKey(urlParamsPrefix ?? chartId)
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>(
    () => {
      if (typeof window !== "undefined") {
        const value = window.localStorage.getItem(storageKey)
        if (value) return JSON.parse(value)
      }
      return {}
    },
  )

  const handleColumnSizingChange = useCallback(
    (updater: Updater<Record<string, number>>) => {
      setColumnSizing(prev => {
        const newVal = typeof updater === "function" ? updater(prev) : updater
        if (typeof window !== "undefined") {
          window.localStorage.setItem(storageKey, JSON.stringify(newVal))
        }
        return newVal
      })
    },
    [storageKey],
  )

  // Column visibility is now controlled by the parent's columnConfig filtering.
  // If a column is in columnConfig, the user explicitly selected it (even if disabled by default).
  const columnVisibility = useMemo(
    () => ({}) as Record<string, boolean>,
    [filteredColumns],
  )

  const tableOptions: TableOptions<TMetric> = useMemo(
    () => ({
      columns: tableColumns,
      data: data ?? [],
      state: {
        sorting,
        pagination: paginationState,
        columnPinning,
        columnOrder,
        rowSelection,
        columnSizing,
        columnVisibility,
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
      onColumnPinningChange: handlePinningChange,
      onColumnOrderChange: handleColumnOrderChange,
      enableRowSelection,
      onRowSelectionChange,
      manualPagination,
      pageCount: manualPagination ? (totalPages ?? -1) : undefined,
      manualSorting: true,
      manualFiltering: true,
      enableColumnResizing: true,
      columnResizeMode: "onChange",
      onColumnSizingChange: handleColumnSizingChange,
      enableMultiSort: MULTI_SORTING_ENABLED,
    }),
    [
      tableColumns,
      sorting,
      paginationState,
      columns,
      data,
      sources,
      compareUniqueKey,
      isComparing,
      columnPinning,
      columnOrder,
      rowSelection,
      enableRowSelection,
      onRowSelectionChange,
      columnSizing,
      handleColumnSizingChange,
      columnVisibility,
      manualPagination,
      totalPages,
    ],
  )

  const table = useReactTable(tableOptions)
  const headerGroups = table.getHeaderGroups()
  const rowModel = table.getRowModel()

  useEffect(() => {
    onRowsRendered?.(metrics)
  }, [metrics, onRowsRendered])
  const tBodyId = `tbody_${chartId}`

  const { isEnabled: isDemoEnabled } = useDemoMode()

  const onRowClick = (data: TMetric) => {
    // Caller-provided handler wins — when set, the default drawer behavior is
    // bypassed entirely. Used by AdBlaster's tabs to toggle row selection on
    // click instead of opening the AdDetails / PostDetails drawer.
    if (onRowClickProp) {
      onRowClickProp(data)
      return
    }
    if (rowClickInDrawer) {
      openDrawer({
        props: {
          metric: data,
        },
        clickSourceId: tBodyId,
      })
    }
  }

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      setColumnOrder(columnOrder => {
        const oldIndex = columnOrder.indexOf(active.id as string)
        const newIndex = columnOrder.indexOf(over.id as string)
        return arrayMove(columnOrder, oldIndex, newIndex) // this is just a splice util
      })
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 3 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, {}),
  )

  return (
    <div className="flex flex-col gap-3">
      {csvExport && (
        <div className="flex justify-end">
          <ExportButton
            onExport={csvExport}
            disabled={isExporting || noData}
            isLoading={isExporting}
          >
            {isExporting && exportProgress
              ? `${exportProgress.message || "Exporting..."} ${Math.round(exportProgress.progress)}%`
              : "Export CSV"}
          </ExportButton>
        </div>
      )}
      <TableTagAssignmentsContext.Provider value={tagAssignmentsContextValue}>
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <div
            className={cn(
              "h-full flex flex-col justify-between transition-all border rounded-system-lg overflow-hidden border-[1px] border-system-blue-600",
              className,
            )}
          >
            <div className="overflow-x-auto scrollbar relative grow shrink">
              <table
                ref={wrapperRef}
                className="min-w-full border-b rounded-lg border-system-blue-600 h-full border-separate border-spacing-0"
              >
                <TableHead
                  headerGroups={headerGroups}
                  sorting={sorting}
                  columnOrder={columnOrder}
                  columnSizingInfo={table.getState().columnSizingInfo}
                />
                <TableBody
                  demoMode={isDemoEnabled}
                  rows={rowModel.rows}
                  colsLength={columns.length}
                  countPerPage={paginationState.pageSize}
                  disableHover={disableHover}
                  columnOrder={columnOrder}
                  onRowClick={onRowClick}
                  highlight={highlightCells}
                  avgData={
                    avgData as
                      | Record<string, Record<string, number>>
                      | undefined
                  }
                  id={tBodyId}
                />
                {summary ? (
                  <TableSummary
                    headerGroups={headerGroups}
                    columnOrder={columnOrder}
                    metric={summary as TMetric}
                    isLoading={isLoading}
                    compareMetric={compareSummaryData as TMetric | undefined}
                    totalLabelColumn={totalLabelColumn}
                  />
                ) : null}
              </table>
            </div>
            {showPagination ? (
              <div className="px-2 py-1.5 grow-0 shrink-0 rounded-b-system-lg rounded-t-none border-b-[1px] border-l-[1px] border-r-[1px] border-system-blue-600">
                <Pagination
                  hideItemsPerPage={hideItemsPerPage ?? false}
                  state={paginationState}
                  total={
                    manualPagination ? (totalPages ?? 1) : table.getPageCount()
                  }
                  onChangeState={state =>
                    setPagination?.(prev => ({ ...prev, ...state }))
                  }
                  onNext={table.nextPage}
                  onPrevious={table.previousPage}
                />
              </div>
            ) : null}
          </div>
        </DndContext>
      </TableTagAssignmentsContext.Provider>
    </div>
  )
}
export default TableVisualization
